import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import BbpsService from "../../services/BbpsService";
import BannerSlider from "../Recharge/BannerSlider";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { data } from "jquery";
import BillDataModal from "./BillDataModal";
import storage from "../../utils/storage";

const BillPaymentForm = () => {
  const navigate = useNavigate();

  const { category } = useParams();

  const [loading, setLoading] = useState(false);
  const [operatorList, setOperatorList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [paramsFields, setParamsFields] = useState([]);
  const [amount, setAmount] = useState();
  const dropdownRef = useRef();

  useEffect(() => {
    fetchOperatorList(category);
    checkLoginStatus();
  }, [category]);

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const checkLoginStatus = () => {
    // Retrieve access token and name from storage
    const token = storage.getAccessToken();
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  };

  const fetchOperatorList = async (category) => {
    setLoading(true);
    try {
      const result = await BbpsService.operatorList(category);
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setOperatorList(responseData.data);
        setFilteredList(responseData.data);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  const [showBillModal, setShowBillModal] = useState(false);
  const [billData, setBillData] = useState();

  // Fetch bill details based on selected operator
  const billFetchHandler = async () => {
    if (!isLoggedIn) {
      Swal.fire("Oops!", "Please login to continue", "error");
      return;
    }

    setLoading(true);

    try {
      const result = await BbpsService.billFetch(
        selectedOperator?.id,
        selectedOperator?.operator_id,
        paramsFields,
        "0",
        "0"
      );
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        setBillData(data);
        // setShowBillModal(true); // Show modal
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  const orderSummaryHandler = () => {
    if (!isLoggedIn) {
      Swal.fire("Oops!", "Please login to continue", "error");
      return;
    }

    let _amount = amount;

    if (!billData.accept_part_pay) {
      _amount = billData?.bill_net_amount;
    }

    if (!_amount) {
      Swal.fire("Oops!", "Please Enter Amount", "error");
      return;
    }

    navigate("/bill-payment/order-summary", {
      state: {
        category,
        logo: `https://static.mobikwik.com/appdata/operator_icons/op${selectedOperator?.operator_id}.png`,
        selectedOperator,
        paramsFields,
        billData: billData,
        amount: _amount,
      },
    });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    const filtered = operatorList.filter((op) =>
      op.operator_name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredList(filtered);
  };

  const handleSelect = (op) => {
    setSelectedOperator(op);
    setSearchText(op.operator_name);
    setParamsFields(op.params || []);
    setFilteredList([]);
  };

  // Hide dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setFilteredList([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <React.Fragment>
      {loading && <LoadingScreen />}

      <div className="row g-4">
        <div className="col-lg-4 col-xxl-5">
          <form id="recharge-bill" method="post">
            {/* Searchable Custom Dropdown with Logo */}

            <div className="pb-2 text-end">
              <img
                src="/assets/images/b_white_logo.png"
                alt="b-logo"
                width={70}
              />
            </div>
            {!billData && (
              <div>
                <div className="mb-3 position-relative" ref={dropdownRef}>
                  <label htmlFor="operator-search" className="form-label">
                    Select Operator
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search operator..."
                    value={searchText}
                    onChange={handleSearch}
                    onFocus={() => setFilteredList(operatorList)}
                    autoComplete="off"
                  />
                  {filteredList.length > 0 && (
                    <ul
                      className="list-group position-absolute w-100 shadow"
                      style={{
                        zIndex: 10,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    >
                      {filteredList.map((op) => (
                        <li
                          key={op.operator_id}
                          className="list-group-item list-group-item-action d-flex align-items-center"
                          onClick={() => handleSelect(op)}
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            src={`https://static.mobikwik.com/appdata/operator_icons/op${op.operator_id}.png`}
                            alt={op.operator_name}
                            className="me-2"
                            style={{
                              width: 30,
                              height: 30,
                              objectFit: "contain",
                            }}
                          />
                          {op.operator_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Dynamic Fields */}
                {paramsFields.map((param, index) => (
                  <div className="mb-3" key={index}>
                    <label htmlFor={param.param_name} className="form-label">
                      {param.label}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id={param.param_name}
                      name={param.param_name}
                      placeholder={param.label}
                      onChange={(e) => {
                        const newParams = [...paramsFields];
                        newParams[index].value = e.target.value;
                        setParamsFields(newParams);
                      }}
                      required
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Bill Details Card */}
            {billData && (
              <div className="card mb-3 shadow-sm rounded-3">
                <div className="card-body">
                  <h5 className="card-title mb-3">Bill Details</h5>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-0">
                        {selectedOperator?.operator_name || "Operator"}
                      </h6>
                      <small className="text-muted">{category || "N/A"}</small>
                    </div>
                    <div className="d-flex align-items-center">
                      {selectedOperator && (
                        <img
                          src={`https://static.mobikwik.com/appdata/operator_icons/op${selectedOperator?.operator_id}.png`}
                          alt={selectedOperator?.operator_name}
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <ul className="list-group list-group-flush mb-3">
                    {billData.customer_id && (
                      <li className="list-group-item d-flex justify-content-between">
                        <span className="text-secondary">Customer Name</span>
                        <span className="fw-bold fs-6">
                          {billData?.customer_id || "N/A"}
                        </span>
                      </li>
                    )}

                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Bill Number</span>
                      <span className="fw-bold fs-6">
                        {billData?.customer_ref_id || "N/A"}
                      </span>
                    </li>

                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Bill Date</span>
                      <span className="fw-bold fs-6">
                        {billData?.bill_date || "N/A"}
                      </span>
                    </li>

                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Due Date</span>
                      <span className="fw-bold fs-6">
                        {billData?.due_date || "N/A"}
                      </span>
                    </li>

                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Bill Amount</span>
                      <span className="fw-bold fs-6">
                        ₹{billData?.bill_amount || "N/A"}
                      </span>
                    </li>

                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Bill Net Amount</span>
                      <span className="fw-bold fs-6">
                        ₹{billData?.bill_net_amount || "N/A"}
                      </span>
                    </li>

                    {billData.accept_part_pay && (
                      <div className="mt-3">
                        <label className="form-label">Enter Amount</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Amount"
                          onChange={(e) => {
                            setAmount(e.target.value);
                          }}
                        />
                      </div>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="d-grid">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (!billData) {
                    billFetchHandler();
                  } else {
                    orderSummaryHandler();
                  }
                }}
              >
                Continue to Pay Bill
              </button>
            </div>
          </form>
        </div>

        <div className="col-lg-8 col-xxl-7">
          <BannerSlider />
        </div>
      </div>

      <BillDataModal
        show={showBillModal}
        onHide={() => setShowBillModal(false)}
        billData={billData}
      />
    </React.Fragment>
  );
};

export default BillPaymentForm;
